import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { brl, dt, daysUntil, statusMeta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Wallet,
  LogOut,
  Search,
  UserPlus,
  DollarSign,
  Clock,
  AlertTriangle,
  CheckCircle2,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  BadgeDollarSign,
  TrendingUp,
  Users,
  Phone,
  MessageCircle,
  FileText,
  KeyRound,
  UserRound,
  ShieldCheck,
  Calculator,
  Sparkles,
  Settings2,
  Activity,
  LayoutDashboard,
  CreditCard,
  ClipboardList,
  UserCog,
  CircleHelp,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import ClientFormDialog from "@/components/ClientFormDialog";
import PaymentDialog from "@/components/PaymentDialog";
import AtrasadosDialog from "@/components/AtrasadosDialog";
import NotificationsBell from "@/components/NotificationsBell";
import ClientDetailSheet from "@/components/ClientDetailSheet";
import CashFlowChart from "@/components/CashFlowChart";
import MonthlyReportDialog from "@/components/MonthlyReportDialog";
import AdminUsersDialog from "@/components/AdminUsersDialog";
import SettingsDialog from "@/components/SettingsDialog";
import ProposalDialog from "@/components/ProposalDialog";
import Logo from "@/components/Logo";
import SupportFab from "@/components/SupportFab";
import { useNavigate } from "react-router-dom";

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "atrasado", label: "Atrasados" },
  { key: "a_vencer", label: "A Vencer" },
  { key: "pendente", label: "Pendentes" },
  { key: "pago", label: "Pagos" },
];

function MetricCard({ icon: Icon, label, value, hint, tone = "blue", testid }) {
  const toneMap = {
    blue: "from-blue-500/20 to-blue-500/0 text-blue-400 border-blue-500/30",
    emerald:
      "from-emerald-500/20 to-emerald-500/0 text-emerald-400 border-emerald-500/30",
    amber:
      "from-amber-500/20 to-amber-500/0 text-amber-400 border-amber-500/30",
    rose: "from-rose-500/20 to-rose-500/0 text-rose-400 border-rose-500/30",
    slate:
      "from-slate-500/20 to-slate-500/0 text-slate-300 border-slate-500/30",
  };

  return (
    <div
      className={`glass-card cyber-metric-card glass-card-hover p-4 sm:p-5 min-w-0 tone-${tone}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-[11px] uppercase tracking-wide text-slate-400 font-semibold truncate">
            {label}
          </div>
        </div>

        <div
          className={`w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-lg bg-gradient-to-br ${toneMap[tone]} border flex items-center justify-center`}
        >
          <Icon className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
        </div>
      </div>

      <div className="min-w-0">
        <div className="metric-value text-lg sm:text-2xl font-bold text-white leading-tight tracking-tight whitespace-nowrap">
          {value}
        </div>

        {hint && (
          <div className="text-[10px] sm:text-[11px] text-slate-500 mt-1 leading-tight truncate">
            {hint}
          </div>
        )}
      </div>
      <svg className={`metric-sparkline spark-${tone}`} viewBox="0 0 92 28" aria-hidden="true">
        <path className="sparkline-glow" d="M2 23 L14 18 L25 20 L37 11 L49 14 L61 7 L73 10 L90 2" />
        <path className="sparkline-line" d="M2 23 L14 18 L25 20 L37 11 L49 14 L61 7 L73 10 L90 2" />
      </svg>
    </div>
  );
}

function DashboardInsights({ summary, clients }) {
  const totalExpected = Math.max(0, Number(summary?.total_paid || 0) + Number(summary?.balance || 0));
  const received = Math.max(0, Number(summary?.total_paid || 0));
  const rate = totalExpected > 0 ? Math.min(100, Math.round((received / totalExpected) * 100)) : 0;
  const totalClients = clients.length;
  const counts = clients.reduce((acc, client) => {
    if (client.status === "pago") acc.paid += 1;
    else if (client.status === "atrasado") acc.late += 1;
    else if (client.status === "a_vencer") acc.due += 1;
    else acc.pending += 1;
    return acc;
  }, { paid: 0, due: 0, late: 0, pending: 0 });
  const active = Math.max(0, totalClients - counts.paid);
  const activeDenom = Math.max(1, active);
  return (
    <div className="dashboard-insights-stack">
      <section className="glass-card cyber-panel dashboard-insight-card" data-testid="collection-rate-panel">
        <header className="dashboard-insight-heading"><Users/><strong>Taxa de recebimento</strong><span title="Calculada a partir dos valores recebidos e saldo devedor">ⓘ</span></header>
        <div className="dashboard-insight-main">
          <div className="dashboard-donut" style={{"--donut-value": `${rate}%`, "--donut-color": "#00dfc0"}}><b>{rate}%</b></div>
          <div className="dashboard-insight-amount"><small>Recebido</small><strong>{brl(received)}</strong><small>de {brl(totalExpected)}</small></div>
        </div>
        <div className="dashboard-progress-item"><div><span>Recebido</span><b>{rate}%</b></div><i><em style={{width:`${rate}%`}}/></i></div>
        <div className="dashboard-progress-item pending"><div><span>Pendente</span><b>{100-rate}%</b></div><i><em style={{width:`${100-rate}%`}}/></i></div>
      </section>
      <section className="glass-card cyber-panel dashboard-insight-card" data-testid="active-client-status-panel">
        <header className="dashboard-insight-heading"><Users/><strong>Clientes ativos</strong></header>
        <div className="dashboard-insight-main compact">
          <div className="dashboard-donut clients-donut" style={{"--donut-value": `${active ? Math.round((counts.due+counts.late)/active*100) : 0}%`, "--donut-color": "#168cff"}}><b>{active}</b><small>de {totalClients}</small></div>
          <div className="dashboard-status-legend">
            <div><i className="legend-green"/> Em dia <b>{counts.paid}</b></div>
            <div><i className="legend-blue"/> A vencer <b>{counts.due}</b></div>
            <div><i className="legend-red"/> Atrasados <b>{counts.late}</b></div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(() => new Date());
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos");
  const [activeSection, setActiveSection] = useState("dashboard");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [paymentCtx, setPaymentCtx] = useState(null); // {client, installment}
  const [detailClient, setDetailClient] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [atrasadosOpen, setAtrasadosOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/clients"),
      ]);
      setSummary(s.data);
      setClients(c.data);
      setLastUpdated(new Date());
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Atualiza os indicadores com dados reais da API sem piscar a tela inteira.
  useEffect(() => {
    let busy = false;
    const refreshLiveData = async () => {
      if (busy || document.visibilityState === "hidden") return;
      busy = true;
      try {
        const [s, c] = await Promise.all([
          api.get("/dashboard/summary"),
          api.get("/clients"),
        ]);
        setSummary(s.data);
        setClients(c.data);
        setLastUpdated(new Date());
      } catch {
        // Mantém os últimos dados válidos se a conexão estiver temporariamente indisponível.
      } finally {
        busy = false;
      }
    };
    const interval = setInterval(refreshLiveData, 1000);
    const onFocus = () => refreshLiveData();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  // Admin: poll pending registrations count every 45s + refresh on window focus
  useEffect(() => {
    if (!["admin", "super_admin"].includes(user?.role)) return;
    const refresh = async () => {
      try {
        const { data } = await api.get("/dashboard/summary");
        setSummary((s) => ({ ...(s || {}), pending_registrations: data.pending_registrations }));
      } catch {}
    };
    const int = setInterval(refresh, 45000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(int);
      window.removeEventListener("focus", onFocus);
    };
  }, [user?.role]);

  const filtered = useMemo(() => {
    const list = clients.filter((c) => {
      const okStatus = filter === "todos" || c.status === filter;
      const okQ =
        !q ||
        c.name.toLowerCase().includes(q.toLowerCase()) ||
        (c.phone || "").includes(q);
      return okStatus && okQ;
    });
    // Ordena por próximo vencimento (mais próximo primeiro).
    // Atrasados (data no passado) ficam no topo; quitados (sem próxima) vão pro fim.
    return list.sort((a, b) => {
      const av = a.next_due_date ? new Date(`${a.next_due_date}T12:00:00`).getTime() : Infinity;
      const bv = b.next_due_date ? new Date(`${b.next_due_date}T12:00:00`).getTime() : Infinity;
      if (av !== bv) return av - bv;
      return a.name.localeCompare(b.name, "pt-BR");
    });
  }, [clients, q, filter]);

  const alerts = useMemo(
    () =>
      clients.filter(
        (c) => c.status === "atrasado" || c.status === "a_vencer"
      ),
    [clients]
  );

  const handleDelete = async (c) => {
    if (!window.confirm(`Excluir cliente "${c.name}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/clients/${c.id}`);
      toast.success("Cliente excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPaymentForNext = (c) => {
    const next = (c.installments || []).find((i) => !i.paid);
    if (!next) {
      toast.info("Todas as parcelas já foram pagas.");
      return;
    }
    setPaymentCtx({ client: c, installment: next });
  };

  const sendReminder = (c) => {
    const next = (c.installments || []).find((i) => !i.paid);
    if (!next) {
      toast.info("Não há parcelas em aberto para lembrar.");
      return;
    }
    const phone = (c.phone || "").replace(/\D/g, "");
    if (!phone) {
      toast.error("Cliente sem telefone. Cadastre um número para enviar o lembrete.");
      return;
    }
    const business = user?.business_name || user?.name || "MS Soluções Financeiras";
    const dueLabel = new Date(`${next.due_date}T12:00:00`).toLocaleDateString("pt-BR");
    const valueLabel = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(next.amount);
    const msg = `Olá ${c.name}, aqui é ${business}. Passando para lembrar do pagamento no valor de ${valueLabel} com vencimento em ${dueLabel}. Qualquer dúvida, é só responder por aqui. Obrigado!`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const scrollToPanel = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectSection = (section) => {
    setActiveSection(section);
    if (section === "dashboard") {
      setFilter("todos");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (section === "clients") {
      setFilter("todos");
      scrollToPanel("clients-panel");
      return;
    }
    if (section === "loans") {
      setFilter("todos");
      scrollToPanel("clients-panel");
      return;
    }
    if (section === "payments") {
      setFilter("pago");
      scrollToPanel("clients-panel");
      return;
    }
    if (section === "collections") {
      setAtrasadosOpen(true);
      return;
    }
    if (section === "simulations") {
      setProposalOpen(true);
      return;
    }
    if (section === "reports") {
      setReportOpen(true);
      return;
    }
    if (section === "users") {
      if (["admin", "super_admin"].includes(user?.role)) setAdminOpen(true);
      else toast.error("Você não tem permissão para gerenciar usuários.");
      return;
    }
    if (section === "settings") {
      navigate("/configuracoes");
      return;
    }
    if (section === "billing") {
      navigate("/planos");
    }
  };

  const sidebarItem = "dashboard-sidebar-item";
  const sidebarClass = (section) => `${sidebarItem}${activeSection === section ? " active" : ""}`;

  return (
    <div className="min-h-screen dashboard-neon-bg dashboard-app-shell">
      <aside className="dashboard-sidebar-shell" aria-label="Navegação principal">
        <div className="dashboard-sidebar-brand">
          <Logo size={34} />
          <div className="min-w-0">
            <strong>MS Soluções</strong>
            <small>PAINEL FINANCEIRO</small>
          </div>
        </div>
        <div className="dashboard-sidebar-section-label">MENU PRINCIPAL</div>
        <nav className="dashboard-sidebar-nav">
          <button type="button" className={sidebarClass("dashboard")} onClick={() => selectSection("dashboard")}><LayoutDashboard/><span>Dashboard</span></button>
          <button type="button" className={sidebarClass("clients")} onClick={() => selectSection("clients")}><Users/><span>Clientes</span></button>
          <button type="button" className={sidebarClass("loans")} onClick={() => selectSection("loans")}><Wallet/><span>Empréstimos</span></button>
          <button type="button" className={sidebarClass("payments")} onClick={() => selectSection("payments")}><CreditCard/><span>Pagamentos</span></button>
          <button type="button" className={sidebarClass("collections")} onClick={() => selectSection("collections")}><ClipboardList/><span>Cobranças</span></button>
          <button type="button" className={sidebarClass("simulations")} onClick={() => selectSection("simulations")}><Calculator/><span>Simulações</span></button>
          <button type="button" className={sidebarClass("reports")} onClick={() => selectSection("reports")}><FileText/><span>Relatórios</span></button>
        </nav>
        <div className="dashboard-sidebar-section-label dashboard-sidebar-admin-label">ADMINISTRAÇÃO</div>
        <nav className="dashboard-sidebar-nav">
          {(["admin", "super_admin"].includes(user?.role)) && <button type="button" className={sidebarClass("users")} onClick={() => selectSection("users")}><UserCog/><span>Usuários</span></button>}
          <button type="button" className={sidebarClass("settings")} onClick={() => selectSection("settings")}><Settings2/><span>Configurações</span></button>
          <button type="button" className={sidebarClass("billing")} onClick={() => selectSection("billing")}><CreditCard/><span>Plano e faturamento</span></button>
        </nav>
        <div className="dashboard-sidebar-support-wrap">
          <SupportFab />
        </div>
      </aside>
      <div className="dashboard-main-column min-w-0 flex-1">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 backdrop-blur-xl bg-[#060A12]/85 border-b border-slate-800/80"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
        data-testid="top-header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 h-16 sm:h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Logo size={34} />
            <div className="min-w-0">
              <div className="text-white font-bold tracking-tight truncate text-sm sm:text-base" style={{ fontFamily: "Outfit" }}>
                {user?.business_name || "MS Soluções Financeiras"}
              </div>
              <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-slate-400 hidden sm:block">
                Painel de Empréstimos
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationsBell
              onOpenClient={(cid) => {
                const c = clients.find((x) => x.id === cid);
                if (c) setDetailClient(c);
              }}
              onOpenAdmin={() => setAdminOpen(true)}
            />
            {!["admin", "super_admin"].includes(user?.role) && (
              <button
                type="button"
                onClick={() => navigate("/planos")}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                  user.plan_state === "trial"
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20"
                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                }`}
                data-testid="header-plan-pill"
              >
                {user.plan_state === "trial" ? (
                  <>
                    <Clock className="w-3.5 h-3.5" />
                    Trial · {user.plan_days_remaining ?? 0}d restantes
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5" />
                    Plano {user.plan_type} · {user.plan_days_remaining ?? 0}d
                  </>
                )}
              </button>
            )}
            <div className="hidden sm:block text-right">
              <div className="text-sm text-slate-200 font-medium" data-testid="header-user-name">
                {user?.name}
              </div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-11 h-11 sm:w-10 sm:h-10 rounded-full bg-slate-800/60 hover:bg-slate-700/60 text-slate-200 shrink-0 relative"
                  data-testid="header-menu-btn"
                >
                  <span className="font-semibold text-base sm:text-sm">
                    {(user?.name || "U").slice(0, 1).toUpperCase()}
                  </span>
                  {["admin", "super_admin"].includes(user?.role) &&
  summary?.pending_registrations > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#060A12]"
                      data-testid="header-pending-badge"
                    >
                      {summary.pending_registrations}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="bg-slate-900 border-slate-700 text-slate-200 min-w-[220px]"
              >
                <DropdownMenuItem disabled className="text-slate-400">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem
                  onClick={() => setSettingsOpen(true)}
                  data-testid="open-settings"
                  className="cursor-pointer py-2.5"
                >
                  <UserRound className="w-4 h-4 mr-2" /> Perfil
                </DropdownMenuItem>
                {["admin", "super_admin"].includes(user?.role) && (
                  <DropdownMenuItem
                    onClick={() => navigate("/configuracoes")}
                    data-testid="open-settings-page"
                    className="cursor-pointer py-2.5"
                  >
                    <Settings2 className="w-4 h-4 mr-2" /> Configurações
                  </DropdownMenuItem>
                )}
            
              
  {["admin", "super_admin"].includes(user?.role) && (
  <DropdownMenuItem
    onClick={() => setAdminOpen(true)}
    data-testid="open-admin-users"
    className="cursor-pointer py-2.5 flex items-center"
  >
    <Users className="w-4 h-4 mr-2" />
    <span className="flex-1">
      Contas cadastradas
    </span>

    {summary?.pending_registrations > 0 && (
      <span
        className="ml-2 min-w-[20px] h-[20px] px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-semibold flex items-center justify-center"
        data-testid="menu-pending-badge"
      >
        {summary.pending_registrations}
      </span>
    )}
  </DropdownMenuItem>
)}
  
                <DropdownMenuItem onClick={logout} data-testid="logout-menu-item" className="cursor-pointer py-2.5">
                  <LogOut className="w-4 h-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 space-y-5 md:space-y-8">
        {/* Hero header — neon map backdrop, animated signal paths and live status */}
        <div className="dashboard-hero-fx">
          <div className="dashboard-worldmap" aria-hidden="true">
            <svg viewBox="0 0 900 260" preserveAspectRatio="xMidYMid meet">
              <defs>
                <radialGradient id="ms-map-glow"><stop offset="0" stopColor="#13bfff" stopOpacity=".55"/><stop offset="1" stopColor="#086bff" stopOpacity="0"/></radialGradient>
                <filter id="ms-map-blur"><feGaussianBlur stdDeviation="3.5"/></filter>
                <pattern id="ms-map-dots" width="8" height="8" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.25" fill="#168fff" opacity=".72"/></pattern>
              </defs>
              <ellipse cx="455" cy="125" rx="330" ry="125" fill="url(#ms-map-glow)"/>
              <g fill="url(#ms-map-dots)" stroke="#168fff" strokeOpacity=".2" strokeWidth="1">
                <path d="M110 53 L153 29 205 36 240 60 232 83 211 91 201 120 177 143 157 128 146 99 124 88Z"/>
                <path d="M220 143 L246 157 255 190 242 220 230 242 216 210 211 178Z"/>
                <path d="M356 47 L389 31 420 38 442 54 470 50 488 69 478 91 452 96 433 119 409 112 391 94 366 88Z"/>
                <path d="M425 120 L457 113 481 133 475 164 459 198 438 215 423 188 414 157Z"/>
                <path d="M492 52 L533 34 589 41 624 59 668 56 708 77 694 98 655 105 630 127 602 117 581 95 545 91 515 78Z"/>
                <path d="M600 133 L636 125 674 145 688 175 664 194 637 184 617 163Z"/>
                <path d="M725 207 L744 198 766 209 758 223 736 222Z"/>
              </g>
              <g className="dashboard-map-routes" fill="none" stroke="#00d9ff" strokeWidth="1.5">
                <path d="M170 90 Q330 5 450 115 T690 80"/>
                <path d="M238 180 Q420 80 580 155 T760 112"/>
                <path d="M320 70 Q470 210 635 70"/>
              </g>
              <g className="dashboard-map-pulses">
                <circle cx="170" cy="90" r="4"/><circle cx="450" cy="115" r="4"/><circle cx="690" cy="80" r="4"/><circle cx="580" cy="155" r="4"/><circle cx="320" cy="70" r="3"/><circle cx="635" cy="70" r="3"/>
              </g>
            </svg>
          </div>
          <div className="dashboard-live-status">
            <strong><span className="dashboard-live-indicator"/> DADOS EM TEMPO REAL</strong>
            <span><Activity/> ONLINE</span>
            <small><Clock/> Atualizado às {lastUpdated.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</small>
            <span><ShieldCheck/> CONEXÃO ESTÁVEL</span>
          </div>
          <div className="relative z-10">
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-blue-400 font-semibold mb-2">
            Painel de Controle
          </div>
          <h1
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight"
            style={{ fontFamily: "Outfit" }}
            data-testid="dashboard-title"
          >
            Olá, {user?.name?.split(" ")[0] || "titular"}.
          </h1>
          <p className="text-slate-400 mt-1 text-xs sm:text-sm">
            Um panorama rápido dos empréstimos, cobranças e pagamentos em andamento.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            <span className="live-data-pill" data-testid="live-data-status">
              <span className="live-data-dot" />
              <Activity className="w-3.5 h-3.5" />
              DADOS DA CONTA
            </span>
            <span className="text-[10px] sm:text-xs text-slate-500">
              Atualizado às {lastUpdated.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })} · sincronização automática
            </span>
          </div>
          </div>
        </div>

        {/* Admin: cadastros pendentes de aprovação */}
        {["admin", "super_admin"].includes(user?.role) &&
  summary?.pending_registrations > 0 && (
    <div
            className="glass-card p-3 sm:p-4 md:p-5 border-blue-500/30 bg-blue-500/5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            data-testid="pending-registrations-banner"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center shrink-0">
                <ShieldCheck className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-blue-200 font-medium">
                  {summary.pending_registrations} nova(s) solicitação(ões) de cadastro aguardando aprovação
                </div>
                <div className="text-xs text-slate-400">
                  Revise e libere o acesso pelo painel administrativo.
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200 self-start sm:self-auto"
              onClick={() => setAdminOpen(true)}
              data-testid="review-pending-btn"
            >
              Revisar agora
            </Button>
          </div>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <div
            className="glass-card cyber-alert-card p-3 sm:p-4 md:p-5 border-amber-500/30 bg-amber-500/5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
            data-testid="alerts-banner"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-lg bg-amber-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-amber-200 font-medium">
                  {alerts.length} cliente(s) com pagamento próximo ou em atraso.
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {alerts.slice(0, 3).map((c) => c.name).join(" · ")}
                  {alerts.length > 3 && ` +${alerts.length - 3}`}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 self-start sm:self-auto"
              onClick={() => setAtrasadosOpen(true)}
              data-testid="alerts-view-btn"
            >
              Ver atrasados
            </Button>
          </div>
        )}

        {/* Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="metrics-grid">
          <MetricCard
  testid="metric-balance"
  icon={TrendingUp}
  label="Saldo Devedor"
  value={brl(summary?.balance ?? 0)}
  hint="Principal a receber"
  tone="amber"
/>

<MetricCard
  testid="metric-total-lent"
  icon={BadgeDollarSign}
  label="Total Emprestado"
  value={brl(summary?.total_lent ?? 0)}
  hint={`${summary?.total_clients ?? 0} clientes ativos`}
  tone="blue"
/>

<MetricCard
  testid="metric-total-paid"
  icon={CheckCircle2}
  label="Total Recebido"
  value={brl(summary?.total_paid ?? 0)}
  hint="Pagamentos registrados"
  tone="emerald"
/>

<MetricCard
  testid="metric-overdue"
  icon={AlertTriangle}
  label="Em Atraso"
  value={summary?.overdue ?? 0}
  hint={`${summary?.upcoming ?? 0} a vencer em breve`}
  tone="rose"
/>
        </div>

        {/* Main cash-flow chart with collection insight cards */}
        <div className="dashboard-analytics-layout">
          <CashFlowChart />
          <DashboardInsights summary={summary} clients={clients} />
        </div>

        {/* Clients table */}
        <div className="glass-card cyber-panel p-3 sm:p-4 md:p-6" data-testid="clients-panel">
          <div className="flex flex-col gap-3 lg:gap-4 mb-4 lg:mb-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-blue-500/15 border border-blue-500/30 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold text-white" style={{ fontFamily: "Outfit" }}>
                    {activeSection === "loans" ? "Empréstimos" : activeSection === "payments" ? "Pagamentos registrados" : "Clientes"}
                  </h2>
                  <p className="text-xs text-slate-500">
                    {filtered.length} de {clients.length} exibidos
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  onClick={() => {
                    setEditing(null);
                    setFormOpen(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-500 text-white h-10 px-3 sm:px-4"
                  data-testid="add-client-btn"
                >
                  <UserPlus className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Novo cliente</span>
                </Button>
                <Button
                  onClick={() => setProposalOpen(true)}
                  variant="outline"
                  className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-10 px-3 sm:px-4"
                  data-testid="open-proposal-btn"
                >
                  <Calculator className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Simular</span>
                </Button>
                <Button
                  onClick={() => setReportOpen(true)}
                  variant="outline"
                  className="border-slate-700 text-slate-200 hover:bg-slate-800 hover:text-white h-10 px-3 sm:px-4"
                  data-testid="open-report-btn"
                >
                  <FileText className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Relatório</span>
                </Button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  placeholder="Buscar por nome ou telefone..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9 h-10 w-full sm:max-w-xs bg-slate-900/60 border-slate-700 focus:border-blue-500"
                  data-testid="search-input"
                />
              </div>
              <div className="-mx-3 sm:mx-0 overflow-x-auto no-scrollbar">
                <Tabs value={filter} onValueChange={setFilter}>
                  <TabsList className="bg-slate-900/60 border border-slate-800 h-10 mx-3 sm:mx-0 inline-flex" data-testid="status-filter-tabs">
                    {FILTERS.map((f) => (
                      <TabsTrigger
                        key={f.key}
                        value={f.key}
                        data-testid={`filter-${f.key}`}
                        className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-slate-300 whitespace-nowrap"
                      >
                        {f.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </Tabs>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-slate-500" data-testid="clients-loading">
              Carregando clientes...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center" data-testid="clients-empty">
              <div className="mx-auto w-14 h-14 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-3">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div className="text-slate-300 font-medium mb-1">Nenhum cliente encontrado</div>
              <div className="text-xs text-slate-500 mb-5">
                Adicione seu primeiro cliente para começar a acompanhar os empréstimos.
              </div>
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="empty-add-client-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" /> Adicionar cliente
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile card list (< md) */}
              <div className="md:hidden space-y-2.5" data-testid="clients-mobile">
                {filtered.map((c) => {
                  const s = statusMeta[c.status] || statusMeta.pendente;
                  const dU = daysUntil(c.next_due_date);
                  return (
                    <div
                      key={c.id}
                      className="rounded-xl border border-slate-800/80 bg-slate-900/40 p-3.5 space-y-3"
                      data-testid={`client-card-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-slate-100 truncate">{c.name}</div>
                          {c.phone && (
                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {c.phone}
                            </div>
                          )}
                        </div>
                        <Badge
                          variant="outline"
                          className={`${s.bg} ${s.color} ${s.border} font-medium shrink-0`}
                          data-testid={`status-${c.id}`}
                        >
                          <span className={`status-dot mr-1.5 ${s.dot}`} />
                          {s.label}
                        </Badge>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-2">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">Empréstimo</div>
                          <div className="text-slate-100 font-mono font-semibold">{brl(c.loan_amount)}</div>
                          <div className="text-[11px] text-slate-500 font-mono">
                            Saldo {brl(c.current_principal ?? c.balance)}
                          </div>
                        </div>
                        <div className="rounded-lg bg-slate-900/60 border border-slate-800 p-2">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500">Vencimento</div>
                          <div className="text-slate-100 font-mono font-semibold">
                            {c.next_due_date ? dt(c.next_due_date) : "Sem vencimento"}
                          </div>
                          {c.next_due_date && (
                            <div
                              className={`text-[11px] font-medium ${
                                (c.days_past_due || 0) > 0
                                  ? "text-rose-400"
                                  : dU <= 3
                                    ? "text-amber-400"
                                    : "text-slate-500"
                              }`}
                            >
                              {(c.days_past_due || 0) > 0
                                ? `${c.days_past_due}d em atraso`
                                : dU === 0
                                  ? "Vence hoje"
                                  : `Em ${dU} dias`}
                            </div>
                          )}
                        </div>
                      </div>

                      {c.status !== "pago" && c.next_due_date && (
                        <div
                          className={`rounded-lg border p-2 text-xs ${
                            (c.days_past_due || 0) > 0
                              ? "border-rose-500/40 bg-rose-500/10"
                              : dU === 0
                                ? "border-emerald-500/40 bg-emerald-500/10"
                                : "border-blue-500/30 bg-blue-500/5"
                          }`}
                          data-testid={`due-today-${c.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-widest text-slate-400">Devido hoje</span>
                            {(c.days_past_due || 0) > 0 ? (
                              <span className="text-[10px] text-rose-300 font-bold">
                                {c.days_past_due} {c.days_past_due === 1 ? "dia" : "dias"} em atraso
                              </span>
                            ) : dU === 0 ? (
                              <span className="text-[10px] text-emerald-300 font-bold">
                                Vence hoje
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-0.5 font-mono font-extrabold text-slate-100 text-base">
                            {brl(c.total_due_today ?? c.next_interest ?? 0)}
                          </div>
                          {(c.late_fee || 0) > 0 && (
                            <div className="text-[10px] text-rose-300/90 mt-0.5 font-mono">
                              inclui multa {brl(c.late_fee)} ({c.days_overdue}d × {brl(c.daily_late_fee)})
                            </div>
                          )}
                        </div>
                      )}

                      <div className="text-xs text-slate-400 flex items-center justify-between">
                        <span>
                          {c.collection_method === "a_vista"
                            ? "À vista"
                            : `${c.installments_count}x ${c.collection_frequency || ""}`}
                        </span>
                        {c.collection_method === "parcelado" && (
                          <span className="font-mono text-slate-500">
                            {brl(c.installment_amount)}/parc
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {c.status !== "pago" && (
                          <Button
                            size="sm"
                            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white h-9"
                            onClick={() => openPaymentForNext(c)}
                            data-testid={`pay-btn-${c.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Pagar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-slate-700 text-slate-200 hover:bg-slate-800 h-9"
                          onClick={() => setDetailClient(c)}
                          data-testid={`view-mobile-${c.id}`}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" /> Detalhes
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-9 w-9 text-slate-400 hover:text-slate-100 shrink-0"
                              data-testid={`row-menu-${c.id}`}
                            >
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="bg-slate-900 border-slate-700 text-slate-200"
                          >
                            <DropdownMenuItem
                              onClick={() => sendReminder(c)}
                              data-testid={`remind-${c.id}`}
                            >
                              <MessageCircle className="w-4 h-4 mr-2" /> Enviar lembrete
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => {
                                setEditing(c);
                                setFormOpen(true);
                              }}
                              data-testid={`edit-${c.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem
                              onClick={() => handleDelete(c)}
                              className="text-rose-400 focus:text-rose-300"
                              data-testid={`delete-${c.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop table (md+) */}
              <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-800/80">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-slate-400 text-xs uppercase tracking-wider">
                      Cliente
                    </TableHead>
                    <TableHead className="text-slate-400 text-xs uppercase tracking-wider">
                      Valor / Saldo
                    </TableHead>
                    <TableHead className="text-slate-400 text-xs uppercase tracking-wider">
                      Cobrança
                    </TableHead>
                    <TableHead className="text-slate-400 text-xs uppercase tracking-wider">
                      Próx. vencimento
                    </TableHead>
                    <TableHead className="text-slate-400 text-xs uppercase tracking-wider">
                      Status
                    </TableHead>
                    <TableHead className="text-right text-slate-400 text-xs uppercase tracking-wider">
                      Ações
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => {
                    const s = statusMeta[c.status] || statusMeta.pendente;
                    const dU = daysUntil(c.next_due_date);
                    return (
                      <TableRow
                        key={c.id}
                        className="border-slate-800/70 hover:bg-slate-800/40 transition-colors"
                        data-testid={`client-row-${c.id}`}
                      >
                        <TableCell>
                          <div className="font-medium text-slate-100">{c.name}</div>
                          {c.phone && (
                            <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {c.phone}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-mono text-slate-100 text-sm">{brl(c.loan_amount)}</div>
                          <div className="text-xs text-slate-500 font-mono">
                            Saldo devedor: {brl(c.current_principal ?? c.balance)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-300">
                            {c.collection_method === "a_vista"
                              ? "À Vista"
                              : `${c.installments_count}x ${c.collection_frequency || ""}`}
                          </div>
                          <div className="text-xs text-slate-500 font-mono">
                            {c.collection_method === "parcelado"
                              ? `${brl(c.installment_amount)} / parcela`
                              : "Pagamento único"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-slate-300 font-mono">
                            {c.next_due_date ? dt(c.next_due_date) : "Sem vencimento"}
                          </div>
                          {c.next_due_date && (
                            <div
                              className={`text-xs mt-0.5 ${
                                (c.days_past_due || 0) > 0
                                  ? "text-rose-400"
                                  : dU <= 3
                                    ? "text-amber-400"
                                    : "text-slate-500"
                              }`}
                            >
                              {(c.days_past_due || 0) > 0
                                ? `${c.days_past_due} dia(s) em atraso`
                                : dU === 0
                                  ? "Vence hoje"
                                  : `Em ${dU} dia(s)`}
                            </div>
                          )}
                          {c.status !== "pago" && c.next_due_date && (
                            <div
                              className={`mt-1 text-xs font-mono ${
                                (c.days_past_due || 0) > 0
                                  ? "text-rose-300"
                                  : dU === 0
                                    ? "text-emerald-300"
                                    : "text-blue-300"
                              }`}
                              data-testid={`due-today-${c.id}`}
                            >
                              {dU === 0 && (c.days_past_due || 0) === 0 && (
                                <span className="inline-block mr-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 px-1.5 py-0 text-[9px] font-bold uppercase tracking-wider">
                                  Vence hoje
                                </span>
                              )}
                              Devido hoje: <span className="font-bold">{brl(c.total_due_today ?? c.next_interest ?? 0)}</span>
                              {(c.late_fee || 0) > 0 && (
                                <span className="text-[10px] text-rose-300/80 ml-1">
                                  · multa {brl(c.late_fee)}
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={`${s.bg} ${s.color} ${s.border} font-medium`}
                            data-testid={`status-${c.id}`}
                          >
                            <span className={`status-dot mr-2 ${s.dot}`} />
                            {s.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {c.status !== "pago" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200 h-8"
                                onClick={() => openPaymentForNext(c)}
                                data-testid={`pay-btn-${c.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Pagar
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 text-slate-400 hover:text-slate-100"
                                  data-testid={`row-menu-${c.id}`}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                align="end"
                                className="bg-slate-900 border-slate-700 text-slate-200"
                              >
                                <DropdownMenuItem
                                  onClick={() => setDetailClient(c)}
                                  data-testid={`view-${c.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-2" /> Detalhes
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => sendReminder(c)}
                                  data-testid={`remind-${c.id}`}
                                >
                                  <MessageCircle className="w-4 h-4 mr-2" /> Enviar lembrete
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setEditing(c);
                                    setFormOpen(true);
                                  }}
                                  data-testid={`edit-${c.id}`}
                                >
                                  <Pencil className="w-4 h-4 mr-2" /> Editar
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(c)}
                                  className="text-rose-400 focus:text-rose-300"
                                  data-testid={`delete-${c.id}`}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" /> Excluir
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </div>
      </main>
      </div>

      <ClientFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        client={editing}
        onSaved={() => {
          setFormOpen(false);
          setEditing(null);
          load();
        }}
      />

      <PaymentDialog
        ctx={paymentCtx}
        onOpenChange={(o) => !o && setPaymentCtx(null)}
        onSaved={() => {
          load();
        }}
      />

      <AtrasadosDialog
        open={atrasadosOpen}
        onOpenChange={setAtrasadosOpen}
        clients={clients}
        onPay={(c) => {
          setAtrasadosOpen(false);
          openPaymentForNext(c);
        }}
        onOpenClient={(c) => {
          setAtrasadosOpen(false);
          setDetailClient(c);
        }}
      />

      <ClientDetailSheet
        client={detailClient}
        onOpenChange={(o) => !o && setDetailClient(null)}
        onPay={(inst) => {
          setPaymentCtx({ client: detailClient, installment: inst });
        }}
        onRemind={() => sendReminder(detailClient)}
        onUndo={async (inst) => {
          try {
            await api.delete(`/clients/${detailClient.id}/payments/${inst.number}`);
            toast.success("Pagamento desfeito");
            const { data } = await api.get(`/clients/${detailClient.id}`);
            setDetailClient(data);
            load();
          } catch (e) {
            toast.error(formatApiError(e));
          }
        }}
      />

      <MonthlyReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        businessName={user?.business_name || user?.name}
      />

      <AdminUsersDialog
        open={adminOpen}
        onOpenChange={(o) => {
          setAdminOpen(o);
          if (!o) load();
        }}
      />

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <ProposalDialog
        open={proposalOpen}
        onOpenChange={setProposalOpen}
        businessName={user?.business_name || user?.name}
      />
    </div>
  );
}
